import { useUser } from '@clerk/clerk-react'
import { Pin } from 'lucide-react'
import DecisionAssistantPanel from '@/components/pages/medication-request/sections/decision-assistant-panel'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useDecisionAssistant } from '@/lib/api/med-requests/decision-assistant'
import { useCurrentInvestigation } from '@/hooks/useCurrentInvestigation'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import usePreviousInvestigations from '@/hooks/usePreviousInvestigations'
import { useQuestionnaire } from '@/hooks/useQuestionnaire'
import usePatientOrderHistory from '@/lib/api/med-requests/order-history'
import { usePatientFiles } from '@/services/patientFiles'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import {
  orderPinnedFirst,
  type PinnableSection,
  usePinnedSections,
  usePinnedSectionsStore
} from '@/lib/stores/pinned-sections'
import type { ParsedQuestionnaire } from '@/types/data'
import { jsonSafeParse } from '@/utils/jsonSafeParse'
import { useDecisionAssistantVisibility } from '@/hooks/useDecisionAssistantVisibility'

const getSections = (
  scrData: Record<string, unknown> | null,
  hasOnHoldHistory: boolean | null,
  hasPreviousOrders?: boolean,
  hasPreviousInvestigations?: boolean,
  hasTestResults?: boolean,
  isInvestigation?: boolean,
  parsedQuestions?: ParsedQuestionnaire[],
  hasPatientFiles?: boolean
) => {
  const { medication } = useMedicationRequestStore()

  const scrDataSubFields = scrData
    ? Object.keys(scrData)
        .filter(key => !['date_of_birth', 'first_name', 'last_name', 'nhs_number', 'postcode'].includes(key))
        .map(key => ({
          id: `scr-section-${key}`,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }))
    : []

  const sections = [
    {
      title: 'Patient data',
      pinKey: 'patient-data' as const,
      subfields: [{ id: 'patient-data', label: 'Patient data' }]
    },
    !isInvestigation
      ? {
          title: 'Safeguarding questions',
          pinKey: 'safeguarding-questions' as const,
          subfields: [{ id: 'safeguarding-questions', label: 'Safeguarding questions' }]
        }
      : null,
    hasPreviousOrders
      ? {
          title: 'Previous medications',
          pinKey: 'previous-orders' as const,
          subfields: [{ id: 'previous-orders', label: 'Previous medications' }]
        }
      : false,
    hasPreviousInvestigations
      ? {
          title: 'Previous investigations',
          pinKey: 'previous-investigations' as const,
          subfields: [{ id: 'previous-investigations', label: 'Previous investigations' }]
        }
      : false,
    hasTestResults
      ? {
          title: 'Test results',
          subfields: [{ id: 'test-results', label: 'Test results' }]
        }
      : false,
    hasOnHoldHistory
      ? {
          title: 'On hold history',
          pinKey: 'on-hold-history' as const,
          subfields: [{ id: 'on-hold-history', label: 'On hold history' }]
        }
      : false,
    !isInvestigation && !!parsedQuestions
      ? {
          title: 'Consultation',
          pinKey: 'consultation' as const,
          subfields: [{ id: 'consultation', label: 'Consultation' }]
        }
      : null,
    !isInvestigation && hasPatientFiles
      ? {
          title: 'Patient files',
          pinKey: 'patient-files' as const,
          subfields: [{ id: 'patient-files', label: 'Patient files' }]
        }
      : null,
    !isInvestigation && scrData && medication?.type !== 'OTC'
      ? {
          title: 'SCR data',
          pinKey: 'scr-data' as const,
          subfields: scrDataSubFields
        }
      : null,
    !isInvestigation
      ? {
          title: 'Prescription details',
          pinKey: 'prescription-details' as const,
          subfields: [{ id: 'prescription-details', label: 'Prescription Details' }]
        }
      : null,
    /*   {
      title: "Prescriber justification",
      subfields: [{ id: "add-justification", label: "Add justification" }],
    },
  */
    medication?.type !== 'OTC' && {
      title: 'Additional notes',
      pinKey: 'additional-notes' as const,
      subfields: [{ id: 'add-notes', label: 'Add Notes' }]
    }
  ].filter(Boolean) as {
    title: string
    pinKey?: PinnableSection
    subfields: Array<{ id: string; label: string }>
  }[]

  return sections
}

function PinToggle({
  medRequestId,
  section,
  sectionTitle
}: {
  medRequestId: string
  section: PinnableSection
  sectionTitle: string
}) {
  const isPinned = usePinnedSections(medRequestId).includes(section)
  const togglePinnedSection = usePinnedSectionsStore(state => state.togglePinnedSection)
  return (
    <button
      type='button'
      onClick={() => togglePinnedSection(medRequestId, section)}
      // The label stays constant; aria-pressed carries the pinned state.
      aria-label={`Pin ${sectionTitle}`}
      aria-pressed={isPinned}
      title={isPinned ? `Unpin ${sectionTitle}` : `Pin ${sectionTitle} to the top`}
      className={`mr-4 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-custom-neutral-200 ${
        isPinned ? 'text-[#153cb7]' : 'text-custom-neutral-600'
      }`}
    >
      <Pin className='size-4' fill={isPinned ? 'currentColor' : 'none'} aria-hidden />
    </button>
  )
}

export const scrollToSection = (id: string) => {
  const element = document.getElementById(id)
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
    window.scrollTo({
      top: elementPosition - 100,
      behavior: 'smooth'
    })
  }
}

const { setScrDataAccordionValue } = useMedicationRequestStore.getState()

export default function NavigationMenu({
  patientId,
  patientOrigin = 'medication-request',
  withoutInvestigationId,
  hasTestResults,
  isInvestigation,
  showPinToggles = false
}: {
  patientId: string
  patientOrigin?: 'medication-request' | 'investigation'
  withoutInvestigationId?: string
  hasTestResults?: boolean
  isInvestigation?: boolean
  /** Show pin toggles that move sections to the top of the med request page. */
  showPinToggles?: boolean
}) {
  const { user } = useUser()
  const isJuniorPrescriber = user?.publicMetadata.roles.includes('JUNIOR_PRESCRIBER')

  const investigation = useCurrentInvestigation(patientOrigin === 'investigation')
  const medRequest = useCurrentMedication(
    !!(patientOrigin === 'medication-request' || investigation?.data?.medicationRequestId),
    investigation?.data?.medicationRequestId,
    patientOrigin === 'medication-request'
  )
  const medRequestData = medRequest?.data
  // Only the med request page (showPinToggles) reorders by pins; other pages skip the subscription.
  const pinnedSections = usePinnedSections(showPinToggles ? medRequestData?.id : undefined)
  const { parsedQuestions } = useQuestionnaire(medRequestData?.questionaire ?? '')

  const { showDecisionAssistant } = useDecisionAssistantVisibility({
    medRequestData,
    patientOrigin,
    isInvestigation
  })

  const { data: previousInvestigationsData } = usePreviousInvestigations({
    patientId,
    enabled: !isJuniorPrescriber
  })
  const previousInvestigations = previousInvestigationsData?.filter(entry => entry.id !== withoutInvestigationId)

  const { data: previousOrders } = usePatientOrderHistory(patientId, !!patientId)
  const scrData = jsonSafeParse(medRequestData?.patient?.scrData)

  // The files section renders on the med-request page only (also when the feed
  // errors — that is where the blocked approval gate is explained).
  const { data: patientFiles, isError: patientFilesError } = usePatientFiles(
    patientOrigin === 'medication-request' ? patientId : undefined
  )

  const regularSections = getSections(
    scrData,
    Boolean(medRequestData?.reasonForHold || medRequestData?.patientsResponseForHold),
    !!previousOrders?.filter(order => order.id !== medRequestData?.id)?.length,
    !!previousInvestigations?.length,
    hasTestResults,
    isInvestigation,
    parsedQuestions,
    !!patientFiles?.length || patientFilesError
  )
  // Mirror the page: pinned sections lead in their regular order.
  const sections = orderPinnedFirst(
    regularSections,
    section => !!section.pinKey && pinnedSections.includes(section.pinKey)
  )

  const decisionAssistant = useDecisionAssistant(medRequestData?.id, showDecisionAssistant)

  return (
    // Cap + scroll the whole sidebar column so a tall DA panel plus the nav list
    // can't exceed the viewport (this column is sticky at top-[88px]).
    <div className='flex flex-col gap-4 min-w-[25vw] max-w-[300px] max-h-[85vh] overflow-y-auto'>
      {showDecisionAssistant && (
        <DecisionAssistantPanel
          outcome={decisionAssistant.outcome}
          unavailable={decisionAssistant.isUnavailable}
          // Keep the spinner showing while the med request itself is still
          // loading, so the panel doesn't flash empty -> loading.
          isLoading={decisionAssistant.isLoading || !!medRequest?.isLoading}
        />
      )}
      <div className='py-2 overflow-y-auto min-h-[270px] max-h-[68vh] flex flex-col rounded-2xl border border-custom-neutral-200 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'>
        <div className='p-6 mb-4'>
          <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Navigation Menu</p>
        </div>

        <Accordion type='single' collapsible>
          {sections.map(section => (
            // Keyed by title so an expanded item stays expanded when pinning reorders the list.
            <AccordionItem key={section.title} value={section.title} className='group'>
              {/* The trigger's h3 header has no flex-1 of its own, so stretch it to keep the pins in a column. */}
              <div className='flex items-center [&>h3]:flex-1 group-data-[state=open]:bg-custom-neutral-700'>
                <AccordionTrigger
                  chevronDisabled={section.subfields.length <= 1}
                  className='py-6 px-7 h-[69px] text-[#393E45] font-medium leading-[133.4%] flex items-center'
                  onClick={() => {
                    if (section.pinKey === 'scr-data') {
                      setScrDataAccordionValue('scr-data')
                    }

                    if (section.subfields.length <= 1 && section.subfields[0]?.id) {
                      scrollToSection(section.subfields[0].id)
                    }
                  }}
                >
                  <span>• {section.title}</span>
                </AccordionTrigger>
                {showPinToggles && medRequestData?.id && section.pinKey && (
                  <PinToggle medRequestId={medRequestData.id} section={section.pinKey} sectionTitle={section.title} />
                )}
              </div>

              {section.subfields.length > 1 && (
                <AccordionContent className='px-14 pb-3'>
                  {section.subfields.map((subfield, subIndex) => (
                    <button
                      type='button'
                      onClick={() => scrollToSection(subfield.id)}
                      key={subIndex}
                      className='flex text-custom-neutral-800 leading-[170%] pt-3 truncate'
                    >
                      • {subfield.label}
                    </button>
                  ))}
                </AccordionContent>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  )
}
